// frontend/src/super-admin/DashboardPage.tsx
//
// Platform-owner landing / overview. Surfaces the cross-tenant metrics from
// GET /api/admin/metrics + the billing watch-list from GET /api/admin/billing/health.

import * as React from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, Users as UsersIcon, Banknote, TrendingUp, Layers, AlertTriangle,
  Clock, FileWarning, ArrowRight,
} from 'lucide-react';
import { api } from '../core/api-client';
import { LoadingState } from '../design-system/components/LoadingState';
import { ErrorState } from '../design-system/components/ErrorState';
import { PageHeader } from '../design-system/components/PageHeader';
import { VISUAL } from '../design-system/visual';

interface PlatformMetrics {
  totals: {
    tenants: { active: number; trial: number; suspended: number; cancelled: number; total: number };
    users: number;
    employees: number;
  };
  signups: Array<{ month: string; count: number }>;
  moduleAdoption: Array<{ module_key: string; tenants: number }>;
  billing: {
    subscriptionsByStatus: Record<string, number>;
    estimatedMrr: { currency: string; amount: number };
    /** Comp / free accounts with an active exemption (migration 043). Excluded from MRR. */
    compedAccounts: number;
  };
  trialConversion: { trialsActive: number; converted: number; conversionRate: number };
}

interface BillingHealth {
  past_due: Array<{ tenant_id: string; tenant_name: string; currency: string; open_amount: number; grace_period_ends_at: string | null; since: string | null }>;
  trials_ending: Array<{ tenant_id: string; tenant_name: string; trial_ends_at: string | null }>;
  open_invoices: { count: number; total: number; overdue_count: number };
}

function fmtMoney(currency: string, amount: number): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${currency} ${amount.toLocaleString()}`;
  }
}

function monthLabel(ym: string): string {
  // ym = "2026-06"
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y, (m ?? 1) - 1, 1));
  return d.toLocaleString(undefined, { month: 'short' });
}

export function DashboardPage() {
  const [metrics, setMetrics] = React.useState<PlatformMetrics | null>(null);
  const [health, setHealth] = React.useState<BillingHealth | null>(null);
  const [err, setErr] = React.useState<string | null>(null);

  function load() {
    setErr(null);
    setMetrics(null);
    setHealth(null);
    Promise.all([
      api<PlatformMetrics>('/api/admin/metrics'),
      api<BillingHealth>('/api/admin/billing/health').catch(() => null), // billing.read may be absent
    ])
      .then(([m, h]) => { setMetrics(m); setHealth(h); })
      .catch((e) => setErr(String(e.message ?? e)));
  }
  React.useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (err) return <ErrorState description={err} onRetry={load} />;
  if (!metrics) return <LoadingState variant="page" />;

  const t = metrics.totals.tenants;
  const maxSignup = Math.max(1, ...metrics.signups.map((s) => s.count));
  const maxAdoption = Math.max(1, ...metrics.moduleAdoption.map((m) => m.tenants));

  return (
    <div>
      <PageHeader
        eyebrow="Platform"
        title="Overview"
        description="A cross-tenant pulse of the whole Worqly platform."
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6 max-w-6xl">
        {/* KPI strip */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi icon={<Building2 className="h-4 w-4" />} label="Tenants" value={t.total}
            caption={`${t.active} active · ${t.trial} trial`} />
          <Kpi icon={<UsersIcon className="h-4 w-4" />} label="Users" value={metrics.totals.users}
            caption={`${metrics.totals.employees} employees`} />
          <Kpi icon={<Banknote className="h-4 w-4" />} label="Est. MRR"
            value={fmtMoney(metrics.billing.estimatedMrr.currency, metrics.billing.estimatedMrr.amount)}
            caption={`${metrics.billing.estimatedMrr.currency} · active subs${(metrics.billing.compedAccounts ?? 0) > 0 ? ` · ${metrics.billing.compedAccounts} comped` : ''}`} />
          <Kpi icon={<TrendingUp className="h-4 w-4" />} label="Trial → active"
            value={`${metrics.trialConversion.conversionRate}%`}
            caption={`${metrics.trialConversion.converted} converted`} />
        </section>

        {/* Tenants-by-status breakdown */}
        <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
          <h3 className={[VISUAL.dashSection, 'mb-4'].join(' ')}>Tenants by status</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatTile label="Active" value={t.active} tone="bg-lime-neon/25 text-lime-700" />
            <StatTile label="Trial" value={t.trial} tone="bg-lavender-100 text-lavender-700" />
            <StatTile label="Suspended" value={t.suspended} tone="bg-coral-100 text-coral-700" />
            <StatTile label="Cancelled" value={t.cancelled} tone="bg-ink/8 text-ink/60 dark:bg-white/10 dark:text-white/60" />
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Signups per month */}
          <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
            <h3 className={[VISUAL.dashSection, 'mb-1'].join(' ')}>Signups</h3>
            <p className="text-[12.5px] text-muted-foreground mb-5">New tenant workspaces, last 12 months.</p>
            <div className="flex items-end gap-1.5 h-40">
              {metrics.signups.map((s) => (
                <div key={s.month} className="flex-1 flex flex-col items-center gap-1.5 min-w-0">
                  <div className="w-full flex items-end justify-center flex-1">
                    <div
                      className="w-full max-w-[24px] rounded-t-md bg-coral/70 dark:bg-coral/60 transition-all"
                      style={{ height: `${Math.max(4, (s.count / maxSignup) * 100)}%` }}
                      title={`${s.month}: ${s.count}`}
                    />
                  </div>
                  <span className="text-[9.5px] text-muted-foreground tabular-nums">{s.count}</span>
                  <span className="text-[9px] uppercase tracking-wide text-muted-foreground/70 truncate w-full text-center">
                    {monthLabel(s.month)}
                  </span>
                </div>
              ))}
            </div>
          </section>

          {/* Module adoption */}
          <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
            <div className="flex items-center gap-2.5 mb-5">
              <Layers className="h-4 w-4 text-coral" strokeWidth={1.8} />
              <h3 className={VISUAL.dashSection}>Module adoption</h3>
            </div>
            {metrics.moduleAdoption.length === 0 ? (
              <p className="text-[13px] text-muted-foreground">No modules enabled across any tenant yet.</p>
            ) : (
              <ul className="space-y-2.5">
                {metrics.moduleAdoption.map((m) => (
                  <li key={m.module_key} className="flex items-center gap-3">
                    <span className="text-[12.5px] font-semibold text-foreground capitalize w-24 shrink-0 truncate">
                      {m.module_key.replace(/[-_]/g, ' ')}
                    </span>
                    <div className="flex-1 h-2 rounded-full bg-muted/50 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-lavender-400 dark:bg-lavender-300"
                        style={{ width: `${(m.tenants / maxAdoption) * 100}%` }}
                      />
                    </div>
                    <span className="text-[12px] font-mono tabular-nums text-muted-foreground w-8 text-right shrink-0">
                      {m.tenants}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {/* Billing health */}
        {health && (
          <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
            <div className="flex items-center justify-between gap-3 mb-5">
              <div className="flex items-center gap-2.5">
                <AlertTriangle className="h-4 w-4 text-coral" strokeWidth={1.8} />
                <h3 className={VISUAL.dashSection}>Billing health</h3>
              </div>
              <Link
                to="/admin/billing/subscriptions"
                className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-coral-700 hover:text-coral-800 dark:text-coral-300"
              >
                Subscriptions <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
              <HealthStat icon={<AlertTriangle className="h-4 w-4" />} label="Past due" value={health.past_due.length}
                tone={health.past_due.length > 0 ? 'bg-rose-500/10 text-rose-700 dark:text-rose-300' : 'bg-muted/50 text-muted-foreground'} />
              <HealthStat icon={<Clock className="h-4 w-4" />} label="Trials ending ≤7d" value={health.trials_ending.length}
                tone={health.trials_ending.length > 0 ? 'bg-coral-100 text-coral-700' : 'bg-muted/50 text-muted-foreground'} />
              <HealthStat icon={<FileWarning className="h-4 w-4" />} label="Open invoices" value={health.open_invoices.count}
                caption={health.open_invoices.overdue_count > 0 ? `${health.open_invoices.overdue_count} overdue` : undefined}
                tone={health.open_invoices.overdue_count > 0 ? 'bg-rose-500/10 text-rose-700 dark:text-rose-300' : 'bg-muted/50 text-muted-foreground'} />
            </div>

            {health.past_due.length > 0 && (
              <div className="mb-4">
                <p className={[VISUAL.eyebrow, 'mb-2'].join(' ')}>Past due workspaces</p>
                <ul className="divide-y divide-border/40 rounded-2xl ring-1 ring-border/40 overflow-hidden">
                  {health.past_due.map((p) => (
                    <li key={p.tenant_id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                      <Link to={`/admin/tenants/${p.tenant_id}`} className="text-[13px] font-semibold text-foreground hover:underline truncate">
                        {p.tenant_name}
                      </Link>
                      <span className="text-[12.5px] font-mono tabular-nums text-muted-foreground shrink-0">
                        {fmtMoney(p.currency, p.open_amount)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {health.trials_ending.length > 0 && (
              <div>
                <p className={[VISUAL.eyebrow, 'mb-2'].join(' ')}>Trials ending soon</p>
                <ul className="divide-y divide-border/40 rounded-2xl ring-1 ring-border/40 overflow-hidden">
                  {health.trials_ending.map((tr) => (
                    <li key={tr.tenant_id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                      <Link to={`/admin/tenants/${tr.tenant_id}`} className="text-[13px] font-semibold text-foreground hover:underline truncate">
                        {tr.tenant_name}
                      </Link>
                      <span className="text-[12px] tabular-nums text-muted-foreground shrink-0">
                        {tr.trial_ends_at ? new Date(tr.trial_ends_at).toLocaleDateString() : '—'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {health.past_due.length === 0 && health.trials_ending.length === 0 && (
              <p className="text-[13px] text-muted-foreground">No past-due workspaces or trials ending this week.</p>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, caption }: { icon: React.ReactNode; label: string; value: number | string; caption?: string }) {
  return (
    <div className={[VISUAL.bento, 'p-5'].join(' ')}>
      <div className="flex items-center gap-2.5 mb-2">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-2xl bg-muted/60 text-muted-foreground">{icon}</span>
        <p className={VISUAL.eyebrow}>{label}</p>
      </div>
      <p className="font-sans font-light text-[2rem] leading-none tracking-[-0.03em] tabular-nums text-foreground">{value}</p>
      {caption && <p className="text-[11.5px] text-muted-foreground mt-2">{caption}</p>}
    </div>
  );
}

function StatTile({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className={['rounded-2xl px-4 py-3.5 ring-1 ring-border/30', tone].join(' ')}>
      <p className="text-[10.5px] font-bold uppercase tracking-[0.12em] opacity-80">{label}</p>
      <p className="font-sans font-light text-[1.7rem] leading-none tracking-[-0.03em] tabular-nums mt-1.5">{value}</p>
    </div>
  );
}

function HealthStat({ icon, label, value, caption, tone }: { icon: React.ReactNode; label: string; value: number; caption?: string; tone: string }) {
  return (
    <div className={['rounded-2xl px-4 py-3.5 ring-1 ring-border/30', tone].join(' ')}>
      <div className="flex items-center gap-2 mb-1.5">
        {icon}
        <p className="text-[10.5px] font-bold uppercase tracking-[0.10em] opacity-90">{label}</p>
      </div>
      <p className="font-sans font-light text-[1.7rem] leading-none tracking-[-0.03em] tabular-nums">{value}</p>
      {caption && <p className="text-[11px] font-semibold mt-1.5 opacity-90">{caption}</p>}
    </div>
  );
}
